import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { providersService } from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import type { DayOfWeek, WeeklyHoursDay } from '../../shared/types';

/**
 * Mon-first order for display. Backend uses 0=Sun … 6=Sat (ISO-like); we
 * keep that on the wire and only re-sort for the UI here.
 */
const DISPLAY_ORDER: { day: DayOfWeek; key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' }[] = [
  { day: 1, key: 'mon' },
  { day: 2, key: 'tue' },
  { day: 3, key: 'wed' },
  { day: 4, key: 'thu' },
  { day: 5, key: 'fri' },
  { day: 6, key: 'sat' },
  { day: 0, key: 'sun' },
];

function findDay(days: WeeklyHoursDay[], target: DayOfWeek): WeeklyHoursDay | undefined {
  return days.find((d) => d.dayOfWeek === target);
}

export function OpeningHoursCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [open, setOpen] = useState(false);

  const hoursQuery = useQuery({
    queryKey: ['provider', 'me', 'hours'],
    queryFn: () => providersService.getMyHours(),
    staleTime: 30 * 1000,
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        mb: 3,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {t('hours.title')}
        </Typography>
        <IconButton
          size="small"
          aria-label={t('hours.edit')}
          onClick={() => setOpen(true)}
          disabled={hoursQuery.isLoading}
          sx={{ color: 'text.secondary' }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      {hoursQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Stack divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
          {DISPLAY_ORDER.map(({ day, key }) => {
            const h = findDay(hoursQuery.data?.days ?? [], day);
            const closed = !h || h.isClosed;
            return (
              <Stack
                key={day}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 1 }}
              >
                <Typography sx={{ flex: 1, color: 'text.secondary' }}>
                  {t(`hours.days.${key}`)}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: closed ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {closed ? t('hours.closed') : `${h!.opensAt} – ${h!.closesAt}`}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}

      <EditHoursDialog
        open={open}
        initial={hoursQuery.data?.days}
        onClose={() => setOpen(false)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['provider', 'me', 'hours'] });
          notify.success(t('hours.saved'));
          setOpen(false);
        }}
        notifyError={(err) => notify.error(translate(err))}
      />
    </Paper>
  );
}

// ── Editor dialog ─────────────────────────────────────────────

const DEFAULT_OPEN = '08:00';
const DEFAULT_CLOSE = '22:00';

function buildDefaultDays(): WeeklyHoursDay[] {
  // Sat (6) + Sun (0) open 08:00–22:00 — matches the backend seed.
  return [0, 1, 2, 3, 4, 5, 6].map((day) => {
    const weekend = day === 0 || day === 6;
    return {
      dayOfWeek: day as DayOfWeek,
      opensAt: weekend ? DEFAULT_OPEN : null,
      closesAt: weekend ? DEFAULT_CLOSE : null,
      isClosed: !weekend,
    };
  });
}

function EditHoursDialog({
  open,
  initial,
  onClose,
  onSaved,
  notifyError,
}: {
  open: boolean;
  initial?: WeeklyHoursDay[];
  onClose: () => void;
  onSaved: () => void;
  notifyError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [days, setDays] = useState<WeeklyHoursDay[]>(() =>
    initial ? sortByDay(initial) : buildDefaultDays(),
  );

  // Re-seed local state every time the dialog opens with the latest
  // server snapshot — otherwise a cancel-then-reopen would render stale
  // edits.
  useEffect(() => {
    if (open) setDays(initial ? sortByDay(initial) : buildDefaultDays());
  }, [open, initial]);

  const save = useMutation({
    mutationFn: () => providersService.updateMyHours(days),
    onSuccess: onSaved,
    onError: notifyError,
  });

  const setDay = (target: DayOfWeek, patch: Partial<WeeklyHoursDay>) => {
    setDays((prev) =>
      prev.map((d) => (d.dayOfWeek === target ? { ...d, ...patch } : d)),
    );
  };

  const toggleOpen = (target: DayOfWeek, willBeOpen: boolean) => {
    if (willBeOpen) {
      // Re-opening a previously closed day — restore sensible defaults so
      // the time inputs have meaningful values.
      const existing = days.find((d) => d.dayOfWeek === target);
      setDay(target, {
        isClosed: false,
        opensAt: existing?.opensAt ?? DEFAULT_OPEN,
        closesAt: existing?.closesAt ?? DEFAULT_CLOSE,
      });
    } else {
      setDay(target, { isClosed: true, opensAt: null, closesAt: null });
    }
  };

  // Form is valid as long as every open day has both times set.
  const invalid = days.some(
    (d) => !d.isClosed && (!d.opensAt || !d.closesAt),
  );

  return (
    <Dialog open={open} onClose={() => !save.isPending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span">
          {t('hours.editTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('hours.editHint')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: 1 }}>
          {DISPLAY_ORDER.map(({ day, key }) => {
            const d = days.find((x) => x.dayOfWeek === day);
            if (!d) return null;
            const isOpen = !d.isClosed;
            return (
              <Stack
                key={day}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 0.5 }}
              >
                <Typography sx={{ width: 110, fontWeight: 500 }}>
                  {t(`hours.days.${key}`)}
                </Typography>
                <Switch
                  size="small"
                  checked={isOpen}
                  onChange={(e) => toggleOpen(day, e.target.checked)}
                  inputProps={{ 'aria-label': t('hours.openLabel') }}
                />
                <TextField
                  type="time"
                  size="small"
                  label={t('hours.opensAt')}
                  value={d.opensAt ?? ''}
                  onChange={(e) => setDay(day, { opensAt: e.target.value })}
                  disabled={!isOpen}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="time"
                  size="small"
                  label={t('hours.closesAt')}
                  value={d.closesAt ?? ''}
                  onChange={(e) => setDay(day, { closesAt: e.target.value })}
                  disabled={!isOpen}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={invalid || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function sortByDay(days: WeeklyHoursDay[]): WeeklyHoursDay[] {
  return [...days].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
}
